/**
 * #705 (T-060) — "Show QA records": records whose key or name carries a QA prefix (Admin › Settings ›
 * qa.hidden_prefixes) are hidden from the portal, lists, search, requests and Approvals for everyone. An Admin
 * may show them for this browser session from the user menu; every read is asked again. PR #57 CI: the switch is
 * a menu item, never a control on the page, so it takes no part in a page's own checkboxes.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getShowQa, setShowQa } from '@/api/client';
import { MenuItem, MenuSeparator } from '@/kit';

const KEY = 'cigs.showQa';
const ADMIN = 'Admin';

/** The menu item's words for this reader, or null when they may not show QA records. */
export function showQaLabel(roles: readonly string[], on: boolean): string | null {
  if (!roles.includes(ADMIN)) return null;
  return on ? 'Hide QA records' : 'Show QA records';
}

function restore(roles: readonly string[]): boolean {
  let on = false;
  try {
    on = roles.includes(ADMIN) && Boolean(JSON.parse(sessionStorage.getItem(KEY) ?? 'false'));
  } catch {
    on = false;
  }
  setShowQa(on);
  return on;
}

/** Restores the switch for this session once the reader's roles are known (renders nothing). */
export function ShowQaRestore({ roles }: { roles: readonly string[] }) {
  const qc = useQueryClient();
  const key = roles.join(',');
  useEffect(() => {
    if (restore(roles)) void qc.invalidateQueries();
  }, [key]);
  return null;
}

export function ShowQaMenuItem({ roles }: { roles: readonly string[] }) {
  const qc = useQueryClient();
  const [on, setOn] = useState(getShowQa());
  const label = showQaLabel(roles, on);
  if (!label) return null;
  const flip = () => {
    const next = !on;
    setShowQa(next);
    setOn(next);
    try {
      sessionStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // storage blocked: the switch still holds for this page
    }
    void qc.invalidateQueries();
  };
  return (
    <>
      <MenuSeparator />
      <MenuItem onClick={flip} data-testid="show-qa-records">
        {on ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
        {label}
      </MenuItem>
    </>
  );
}
